import { useWebSocketStore } from '../stores/websocketStore';

type EventCallback = (data: any) => void;

export class WebSocketService {
  private ws: WebSocket | null = null;
  private url: string;
  private eventHandlers: Map<string, EventCallback[]> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private currentFamilyId: string | null = null;

  constructor(baseUrl: string, wsBaseUrl?: string) {
    const effectiveBase = wsBaseUrl || baseUrl;
    const wsUrl = effectiveBase.replace('http://', 'ws://').replace('https://', 'wss://');
    this.url = `${wsUrl}/ws`;
  }

  connect() {
    const { setStatus, setConnected, setError } = useWebSocketStore.getState();
    
    try {
      setStatus('connecting');
      this.ws = new WebSocket(this.url);

      this.ws.onopen = () => {
        console.log('🎉 WebSocket connected');
        setStatus('connected');
        setConnected(true);
        setError(null);
        this.reconnectAttempts = 0;
        this.emit('connected', {});
        
        // Resubscribe to family channel if we were subscribed before
        if (this.currentFamilyId) {
          this.switchFamily(this.currentFamilyId);
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📨 WebSocket message received:', data);
          
          if (data.type) {
            this.emit(data.type, data);
          }
        } catch (error) {
          console.error('❌ Failed to parse WebSocket message:', error);
        }
      };

      this.ws.onerror = (error) => {
        if (this.ws?.readyState === WebSocket.OPEN) {
          console.warn('⚠️ WebSocket error event while connected; ignoring status downgrade');
          return;
        }

        console.error('❌ WebSocket error:', error);
        setStatus('error');
        setConnected(false);
        setError('Connection error');
        this.emit('error', error);
      };

      this.ws.onclose = () => {
        console.log('❌ WebSocket disconnected');
        setStatus('disconnected');
        setConnected(false);
        this.emit('disconnected', {});
        this.attemptReconnect();
      };
    } catch (error) {
      console.error('❌ Failed to create WebSocket:', error);
      setStatus('error');
      setError('Failed to connect');
    }
  }

  private attemptReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
      
      console.log(`🔄 Attempting reconnect ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`);
      
      this.reconnectTimeout = setTimeout(() => {
        this.connect();
      }, delay);
    } else {
      console.error('❌ Max reconnect attempts reached');
      useWebSocketStore.getState().setError('Max reconnect attempts reached');
    }
  }

  disconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    
    const { setStatus, setConnected } = useWebSocketStore.getState();
    setStatus('disconnected');
    setConnected(false);
  }

  switchFamily(familyId: string) {
    this.currentFamilyId = familyId;
    
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log(`🔄 Switching to family: ${familyId}`);
      this.send({
        type: 'subscribe',
        channel: `family:${familyId}`
      });
    }
  }

  send(data: any) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      console.warn('⚠️ WebSocket not connected, cannot send message');
    }
  }

  on(event: string, callback: EventCallback) {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event)!.push(callback);
  }

  off(event: string, callback: EventCallback) {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      const index = handlers.indexOf(callback);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  private emit(event: string, data: any) {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach(handler => handler(data));
    }
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }
}
